/**
 * @module utils/qbLinkage/handlers/shared
 * @description Handler 共用：base+draft 双写、Record 裁剪、用户配置存在性判断
 */

import type { IDescriptionSetting } from '../../../../types/settings';
import type { IFieldSnapshot, IQBChangeSet } from '../types';
import { fieldKey } from '../fieldSnapshot';
import { PERCENTAGE_PREFIX } from './columnSort/untils/keys';

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** base/draft 任一层存在该 key（含 undefined 占位）即视为用户触及过 */
export function hasConfigInLayers(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  key: string,
): boolean {
  return key in draft || key in base;
}

/** Record 型配置：任一层存在且至少有一个子 key */
export function hasNonEmptyRecordInLayers(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  key: string,
): boolean {
  const raw = key in draft ? draft[key] : base[key];
  if (!isPlainRecord(raw)) {
    return false;
  }
  return Object.keys(raw).length > 0;
}

/** 数组型配置：任一层存在且为非空数组 */
export function hasNonEmptyArrayInLayers(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  key: string,
): boolean {
  const raw = key in draft ? draft[key] : base[key];
  return Array.isArray(raw) && raw.length > 0;
}

export function hasAnyConfigInLayers(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return keys.some(key => hasConfigInLayers(base, draft, key));
}

export function patchBoth(
  base?: Record<string, unknown>,
  draft?: Record<string, unknown>,
): { base?: Record<string, unknown>; draft?: Record<string, unknown> } {
  if (!base && !draft) {
    return {};
  }
  return {
    base: base ? { ...base } : undefined,
    draft: draft ? { ...draft } : undefined,
  };
}

export function mergeLayer(
  layer: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  return { ...layer, ...patch };
}

export function pruneRecordKeys(
  record: Record<string, unknown> | undefined,
  predicate: (key: string) => boolean,
): Record<string, unknown> | undefined {
  if (!record || typeof record !== 'object') {
    return undefined;
  }
  const next: Record<string, unknown> = {};
  let changed = false;
  for (const [key, value] of Object.entries(record)) {
    if (predicate(key)) {
      changed = true;
      continue;
    }
    next[key] = value;
  }
  return changed ? next : undefined;
}

/** 列占比虚拟列 column_name，与 DescriptionSetting / column_sort 一致 */
function percentVirtualColumnName(metricName: string): string {
  return `${PERCENTAGE_PREFIX}${metricName}`;
}

/** description sub-key：无后缀与带 `_0`/`_1` 角色后缀两种形态 */
function descriptionKeyVariants(
  tableCode: string,
  columnName: string,
  roleSuffix: '0' | '1',
): string[] {
  const prefix = `${tableCode}_${columnName}`;
  return [prefix, `${prefix}_${roleSuffix}`];
}

function pushDescriptionKeyMigration(
  renames: Array<{ prev: string; next: string }>,
  tableCode: string,
  prevColumnName: string,
  nextColumnName: string,
  roleSuffix: '0' | '1',
): void {
  const prevKeys = descriptionKeyVariants(tableCode, prevColumnName, roleSuffix);
  const nextKeys = descriptionKeyVariants(tableCode, nextColumnName, roleSuffix);
  for (let i = 0; i < prevKeys.length; i += 1) {
    renames.push({ prev: prevKeys[i], next: nextKeys[i] });
  }
}

export function descriptionKeysForField(
  snap: IFieldSnapshot,
  roleSuffix: '0' | '1',
): string[] {
  const tableCode = snap.table_code || 't0';
  const keys = descriptionKeyVariants(tableCode, snap.column_name, roleSuffix);
  // 指标移除时需同步清理列占比虚拟列 description（column_name 为 `% {metric}`）
  if (roleSuffix === '0') {
    keys.push(
      ...descriptionKeyVariants(
        tableCode,
        percentVirtualColumnName(snap.column_name),
        '0',
      ),
    );
  }
  return keys;
}

export function pruneDescriptionForRemoved(
  description: IDescriptionSetting | undefined,
  removed: IFieldSnapshot[],
  roleSuffix: '0' | '1',
): IDescriptionSetting | undefined {
  if (!description) {
    return undefined;
  }
  const keysToDrop = new Set<string>();
  for (const snap of removed) {
    for (const k of descriptionKeysForField(snap, roleSuffix)) {
      keysToDrop.add(k);
    }
  }
  return pruneRecordKeys(description, (key) => keysToDrop.has(key));
}

export function migrateRecordKeys(
  record: Record<string, unknown> | undefined,
  renames: Array<{ prev: string; next: string }>,
): Record<string, unknown> | undefined {
  if (!record) {
    return undefined;
  }
  let next = { ...record };
  let changed = false;
  for (const { prev, next: nxt } of renames) {
    if (prev in next) {
      next[nxt] = next[prev];
      delete next[prev];
      changed = true;
    }
  }
  return changed ? next : undefined;
}

export function migrateDescriptionIdentity(
  description: IDescriptionSetting | undefined,
  renamed: Array<{
    snap: IFieldSnapshot;
    prevColumnName: string;
    roleSuffix: '0' | '1';
  }>,
): IDescriptionSetting | undefined {
  if (!description) {
    return undefined;
  }
  const renames: Array<{ prev: string; next: string }> = [];
  for (const r of renamed) {
    const tc = r.snap.table_code || 't0';
    pushDescriptionKeyMigration(
      renames,
      tc,
      r.prevColumnName,
      r.snap.column_name,
      r.roleSuffix,
    );
    // 指标改名时同步迁移列占比虚拟列 description key
    if (r.roleSuffix === '0') {
      pushDescriptionKeyMigration(
        renames,
        tc,
        percentVirtualColumnName(r.prevColumnName),
        percentVirtualColumnName(r.snap.column_name),
        '0',
      );
    }
  }
  return migrateRecordKeys(description, renames);
}

export function fieldSettingKey(snap: IFieldSnapshot): string {
  return fieldKey(snap);
}

/**
 * 聚合删指 + 明细删 raw 列 — 表格配置 prune 共用（columnPercentage、statistics 等）。
 * conditional_format 在明细模式须单独只用 removedRawColumns，不可直接调用本函数，见 conditionalFormat handler。
 */
export function metricLikeRemovedFields(changeSet: IQBChangeSet): IFieldSnapshot[] {
  return [
    ...changeSet.removedMetrics,
    ...(changeSet.removedRawColumns ?? []),
  ];
}

/** 自定义字段（column_name 可随 QB 改名） */
export function isCustomQBField(snap: IFieldSnapshot): boolean {
  if (snap.column_custom === true) {
    return true;
  }
  return !snap.table_id && !!snap.sql_expression;
}

export interface IFieldSettingIdentityRename {
  prev: string;
  next: string;
  snap: IFieldSnapshot;
}

export function migrateFieldSettingIdentity(
  record: Record<string, unknown> | undefined,
  renames: IFieldSettingIdentityRename[],
): Record<string, unknown> | undefined {
  if (!record || !renames.length) {
    return undefined;
  }
  let next = { ...record };
  let changed = false;
  for (const { prev, next: nxt, snap } of renames) {
    if (!(prev in next)) {
      continue;
    }
    const raw = next[prev];
    next[nxt] = isPlainRecord(raw)
      ? {
          ...raw,
          column_name: snap.column_name,
          table_code: snap.table_code || 't0',
          table_id: snap.table_id,
        }
      : raw;
    delete next[prev];
    changed = true;
  }
  return changed ? next : undefined;
}
